"use client";

import Image from "next/image";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const ADVANCE_MS = 4500;
const SWIPE_PX = 56;

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return reduced;
}

type HeroGalleryProps = {
  images: string[];
  fallbackSrc?: string;
  alt?: string;
  sizes?: string;
};

type Phase = "idle" | "armed" | "fade";

export function HeroGallery({
  images,
  fallbackSrc = "/michi.jpg",
  alt = "Michael beim Feiern",
  sizes = "(max-width: 768px) 100vw, (max-width: 1200px) 42vw, 520px",
}: HeroGalleryProps) {
  const paths = useMemo(
    () => (images.length > 0 ? images : [fallbackSrc]),
    [images, fallbackSrc],
  );
  const count = paths.length;
  const reducedMotion = usePrefersReducedMotion();

  const indexRef = useRef(0);
  const [slotSrc, setSlotSrc] = useState<[string, string]>(() => [
    paths[0],
    paths[count > 1 ? 1 % count : 0],
  ]);
  const [visibleSlot, setVisibleSlot] = useState<0 | 1>(0);
  const [phase, setPhase] = useState<Phase>("idle");
  const phaseRef = useRef<Phase>("idle");
  const [incomingSlot, setIncomingSlot] = useState<0 | 1 | null>(null);
  const pendingIndexRef = useRef<number | null>(null);
  /** Slot fading in — ref avoids stale transitionEnd closures */
  const fadeSlotRef = useRef<0 | 1 | null>(null);

  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const completeTransition = useCallback(() => {
    const slot = fadeSlotRef.current;
    const pendingIdx = pendingIndexRef.current;
    if (slot === null || pendingIdx === null) return;

    indexRef.current = pendingIdx;
    setVisibleSlot(slot);
    pendingIndexRef.current = null;
    fadeSlotRef.current = null;
    setIncomingSlot(null);
    setPhase("idle");
  }, []);

  const goToIndex = useCallback(
    (nextIndex: number) => {
      if (count <= 1 || reducedMotion) return;
      if (phaseRef.current !== "idle") return;

      const outgoing = visibleSlot;
      const incoming = outgoing === 0 ? 1 : 0;

      pendingIndexRef.current = nextIndex;
      setIncomingSlot(incoming);

      setSlotSrc((prev) => {
        const next = [...prev] as [string, string];
        next[incoming] = paths[nextIndex];
        return next;
      });
      setPhase("armed");
    },
    [count, paths, reducedMotion, visibleSlot],
  );

  useEffect(() => {
    if (phase !== "armed" || incomingSlot === null) return;

    const src = slotSrc[incomingSlot];
    let cancelled = false;

    const startFade = () => {
      if (cancelled || phaseRef.current !== "armed") return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled && phaseRef.current === "armed") {
            fadeSlotRef.current = incomingSlot;
            setPhase("fade");
          }
        });
      });
    };

    const img = new window.Image();
    img.src = src;
    if (img.complete && img.naturalWidth > 0) {
      startFade();
    } else {
      img.onload = () => startFade();
      img.onerror = () => startFade();
    }

    return () => {
      cancelled = true;
    };
  }, [phase, incomingSlot, slotSrc]);

  useEffect(() => {
    if (count <= 1 || reducedMotion) return;
    const id = window.setInterval(() => {
      const next = (indexRef.current + 1) % count;
      goToIndex(next);
    }, ADVANCE_MS);
    return () => window.clearInterval(id);
  }, [count, goToIndex, reducedMotion]);

  const goNext = useCallback(() => {
    const next = (indexRef.current + 1) % count;
    goToIndex(next);
  }, [count, goToIndex]);

  const goPrev = useCallback(() => {
    const prev = (indexRef.current - 1 + count) % count;
    goToIndex(prev);
  }, [count, goToIndex]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0]?.clientX ?? null;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const startX = touchStartX.current;
    if (startX === null) return;
    const endX = e.changedTouches[0]?.clientX;
    touchStartX.current = null;
    if (endX === undefined) return;
    const dx = endX - startX;
    if (Math.abs(dx) < SWIPE_PX) return;
    if (dx < 0) goNext();
    else goPrev();
  };

  const transitionClass = reducedMotion
    ? ""
    : "transition-opacity duration-700 ease-out";

  const opacityForSlot = (slot: 0 | 1): number => {
    if (count <= 1 || reducedMotion) return slot === 0 ? 1 : 0;

    if (phase === "idle") {
      return slot === visibleSlot ? 1 : 0;
    }

    if (phase === "armed") {
      return slot === visibleSlot ? 1 : 0;
    }

    if (incomingSlot === null) return slot === visibleSlot ? 1 : 0;
    return slot === incomingSlot ? 1 : 0;
  };

  const zForSlot = (slot: 0 | 1): number => {
    if (count <= 1 || reducedMotion) return slot === 0 ? 10 : 0;
    if (phase === "fade" && incomingSlot !== null) {
      return slot === incomingSlot ? 20 : 10;
    }
    return slot === visibleSlot ? 10 : 0;
  };

  const frameClass =
    "relative aspect-[4/5] w-full touch-pan-y overflow-hidden rounded-3xl shadow-[0_28px_68px_-22px_rgb(0_0_0/0.72),0_0_0_1px_rgb(255_255_255/0.12)] ring-1 ring-white/15";

  if (count <= 1 || reducedMotion) {
    return (
      <div className={frameClass}>
        <Image
          src={paths[0]}
          alt={alt}
          fill
          priority
          sizes={sizes}
          className="object-cover"
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/55 via-black/15 to-transparent md:opacity-90"
          aria-hidden
        />
      </div>
    );
  }

  return (
    <div
      className={frameClass}
      role="region"
      aria-roledescription="Karussell"
      aria-label={alt}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {([0, 1] as const).map((slot) => (
        <Image
          key={slot}
          src={slotSrc[slot]}
          alt={slot === visibleSlot && phase === "idle" ? alt : ""}
          fill
          sizes={sizes}
          priority={slot === visibleSlot && phase === "idle"}
          className={`absolute inset-0 object-cover ${transitionClass}`}
          style={{
            opacity: opacityForSlot(slot),
            zIndex: zForSlot(slot),
          }}
          aria-hidden={slot !== visibleSlot || phase !== "idle"}
          onTransitionEnd={(e) => {
            if (e.propertyName !== "opacity") return;
            if (phaseRef.current !== "fade") return;
            if (fadeSlotRef.current !== slot) return;
            completeTransition();
          }}
        />
      ))}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-30 h-2/5 bg-gradient-to-t from-black/55 via-black/15 to-transparent md:opacity-90"
        aria-hidden
      />
    </div>
  );
}
